import { Typography, Upload, Button, message, Empty, Spin, Card, Image, Tooltip } from 'antd';
import { UploadOutlined, CopyOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

/** 顯示用檔名：優先用 title；遷移資料 title 為空時，取 URL 末段並還原 percent-encoding（避免亂碼）。 */
function displayName(m: { title: string | null; url: string }): string {
  if (m.title) return m.title;
  const last = m.url.split('/').pop() ?? m.url;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

/** 媒體庫：看 R2 上的圖片、上傳、複製網址（仿 WordPress 媒體庫）。 */
export default function MediaPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['media'], queryFn: api.mediaList });

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadImage(file),
    onSuccess: () => {
      message.success('已上傳');
      qc.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const copy = (url: string) => {
    navigator.clipboard.writeText(url);
    message.success('已複製網址');
  };

  const items = data?.items ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Typography.Title level={4} className="!mb-0">
          媒體庫
        </Typography.Title>
        <Upload
          showUploadList={false}
          accept="image/*"
          beforeUpload={(file) => {
            upload.mutate(file);
            return false;
          }}
        >
          <Button type="primary" icon={<UploadOutlined />} loading={upload.isPending}>
            上傳圖片
          </Button>
        </Upload>
      </div>

      {isLoading ? (
        <Spin className="block py-24 text-center" />
      ) : items.length === 0 ? (
        <Empty description="尚無媒體，上傳或遷移後會出現在這裡" />
      ) : (
        <Image.PreviewGroup>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {items.map((m) => {
              const name = displayName(m);
              return (
              <Card
                key={m.id}
                size="small"
                styles={{ body: { padding: 8 } }}
                cover={
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    <Image src={m.url} alt={name} width="100%" height="100%" className="object-cover" />
                  </div>
                }
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="truncate text-xs text-gray-500" title={name}>
                    {name}
                  </span>
                  <Tooltip title="複製網址">
                    <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copy(m.url)} />
                  </Tooltip>
                </div>
              </Card>
              );
            })}
          </div>
        </Image.PreviewGroup>
      )}
    </div>
  );
}
